import useEForm from '@/hooks/useEform'
import BaseEForm from './BaseEform'
import EFormAccordion from './EFormAccordion'
import { Fragment } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Tooltip as ReactTooltip } from 'react-tooltip'
import InfoIcon from '@/assets/svgs/information-circle.svg?react'

const snakeCase = s =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')

const parseDescription = description => {
  if (!description) return null
  if (!description.startsWith('<root>')) return description
  const match = description.match(/<label>description<\/label>:<b>(.*?)<\/b>/)
  return match?.[1] ?? null
}

const SectionTitle = ({ id, title, description, isRequired }) => {
  const desc = parseDescription(description)
  const tooltipId = desc ? `section-desc-${id}` : null
  return (
    <div className="flex items-center gap-1">
      {isRequired && (
        <span className="h-4 mr-1 font-black text-red-50" data-testid="eform-label-asterisk">
          *
        </span>
      )}
      <span>{title}</span>
      {tooltipId && (
        <>
          <span
            className="inline-flex items-center ml-1 cursor-default [&_svg]:w-[18px] [&_svg]:h-[18px] [&_svg]:text-grey-50 hover:[&_svg]:text-grey-10"
            data-tooltip-id={tooltipId}
          >
            <InfoIcon />
          </span>
          <ReactTooltip
            id={tooltipId}
            content={desc}
            className="w-[280px]! whitespace-normal! leading-4! text-left! text-[12px]!"
            place="top"
            opacity={1}
            noArrow
          />
        </>
      )}
    </div>
  )
}

SectionTitle.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  isRequired: PropTypes.bool,
}

const ObjectFieldRenderer = ({
  testId,
  field,
  fieldPath,
  isRequired,
  fieldParentProps,
  level,
  payload,
  arrIdx = 0,
}) => {
  const { getFieldValue } = useEForm()
  const obtainedFieldValues = getFieldValue(payload, fieldParentProps)
  const isCreated =
    typeof obtainedFieldValues === 'object' &&
    obtainedFieldValues !== null &&
    !Array.isArray(obtainedFieldValues)

  if (level === 1 && !isRequired) {
    if (!isCreated) return null
    return (
      <div
        className="col-span-2 w-full sm:pt-0 pt-2 sm:[&:not(:last-child)]:pb-4 [&:not(:last-child)]:pb-0 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-grey-90"
        data-testid={`${testId}__accordion`}
      >
        <div className="flex items-center w-full my-2.5">
          <div className="my-2.5 type-header-4 text-grey-10">
            <SectionTitle id={testId} title={field.title} description={field.description} />
          </div>
        </div>
        <div className="eformv2__root-object-content">
          <EFormRenderer
            schema={field}
            path={fieldPath}
            level={level + 1}
            parentProps={fieldParentProps}
            payload={payload}
            arrIdx={arrIdx}
          />
        </div>
      </div>
    )
  }

  if (level === 1 && isRequired) {
    return (
      <div
        className="col-span-2 w-full sm:pt-0 pt-4 sm:[&:not(:last-child)]:pb-4 [&:not(:last-child)]:pb-0 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-grey-90"
        data-testid={`${testId}__create-accordion`}
      >
        <div className="my-2.5 type-header-4 text-grey-10">
          <SectionTitle
            id={testId}
            title={field.title}
            description={field.description}
            isRequired
          />
        </div>
        <div className="eformv2__root-object-content">
          <EFormRenderer
            schema={field}
            path={fieldPath}
            level={level + 1}
            parentProps={fieldParentProps}
            payload={payload}
            arrIdx={arrIdx}
          />
        </div>
      </div>
    )
  }

  // level > 1
  return (
    <div
      key={`${fieldPath}-object`}
      className={clsx('col-span-2 w-full', { optional: !isRequired })}
    >
      <EFormAccordion
        testId={testId}
        title={field.title}
        subTitle={fieldPath}
        level={level}
        isRequired={isRequired}
        fieldType="object"
        isCreated={isCreated}
        description={field.description}
      >
        <EFormRenderer
          schema={field}
          path={fieldPath}
          level={level + 1}
          parentProps={fieldParentProps}
          payload={payload}
          arrIdx={arrIdx}
        />
      </EFormAccordion>
    </div>
  )
}

ObjectFieldRenderer.propTypes = {
  testId: PropTypes.string,
  field: PropTypes.object,
  fieldPath: PropTypes.string,
  isRequired: PropTypes.bool,
  fieldParentProps: PropTypes.array,
  level: PropTypes.number,
  payload: PropTypes.object,
  arrIdx: PropTypes.number,
}

export const EFormRenderer = ({
  schema,
  path = '',
  level = 1,
  parentProps = [],
  payload,
  arrIdx = 0,
}) => {
  const { getFieldValue } = useEForm()

  if (!payload || !schema) return null

  const renderFields = (properties, required, currentPath, parentProps, payload, level) => {
    return Object.keys(properties).map(key => {
      const field = properties[key]

      const visibilityRules = field['x-uioptions']?.['visibility-rules']
      if (visibilityRules) {
        const conditionMet = visibilityRules.and.every(({ fieldKey, fieldValue }) => {
          const siblingValue = getFieldValue(payload, [...parentProps, fieldKey])
          return Array.isArray(siblingValue)
            ? siblingValue.includes(fieldValue)
            : siblingValue === fieldValue
        })
        if (!conditionMet) return null
      }

      const isRequired = required?.includes(key)
      const fieldPath = currentPath ? `${currentPath} / ${field.title}` : `${field.title}`
      let fieldType = Array.isArray(field.type) ? field.type.find(d => d !== 'null') : field.type
      fieldType = field['x-uitype'] ?? fieldType
      const fieldParentProps = [...parentProps, key]
      const objTestId = snakeCase(key + '-' + level)

      if (fieldType === 'object' && field.properties) {
        return (
          <ObjectFieldRenderer
            key={`${key}-${level}`}
            testId={objTestId}
            field={field}
            fieldPath={fieldPath}
            isRequired={isRequired}
            fieldParentProps={fieldParentProps}
            level={level}
            payload={payload}
            arrIdx={arrIdx}
          />
        )
      }

      if (fieldType === 'array' && field.items?.properties) {
        return renderArrayFields(field, fieldPath, isRequired, fieldParentProps, level)
      }

      return renderBaseFields(field, fieldPath, fieldType, isRequired, fieldParentProps, level)
    })
  }

  const renderArrayFields = (field, fieldPath, isRequired, fieldParentProps, level) => {
    const gotFieldValues = getFieldValue(payload, fieldParentProps)
    const hasFieldValues = Array.isArray(gotFieldValues) && gotFieldValues.length > 0
    // At level 1, optional empty arrays are standalone sections — skip them entirely.
    // At level > 1, always render the wrapper so it acts as a full-width spacer
    // between adjacent base fields (e.g. bess_used / sync_genset_used).
    if (!hasFieldValues && !isRequired && level === 1) return null
    return (
      <div
        key={`${fieldPath}-array`}
        className={clsx('col-span-2 w-full mb-1.25 pb-1.25', { 'max-phone:col-span-1': level > 1 })}
      >
        {renderArrayItems(field, fieldPath, fieldParentProps, isRequired, level)}
      </div>
    )
  }

  const renderArrayItems = (field, fieldPath, fieldParentProps, isRequired, level) => {
    const gotFieldValues = getFieldValue(payload, fieldParentProps)
    const fieldValues =
      Array.isArray(gotFieldValues) && gotFieldValues.length > 0 ? gotFieldValues : [null]
    const hasFieldValues = fieldValues.filter(fiv => fiv !== null).length > 0
    const fieldType =
      field.type && Array.isArray(field.type) ? field.type.find(d => d !== 'null') : field.type
    return (
      <Fragment>
        {field?.items?.properties
          ? fieldValues.map((_, arrIdx) => (
              <EFormAccordion
                key={`${field.title}_${arrIdx}`}
                testId={`${snakeCase(field?.title)}_${arrIdx}`}
                title={field.title}
                subTitle={`${fieldPath} #${arrIdx + 1}`}
                level={level}
                arrIdx={arrIdx}
                fieldType={fieldType ?? 'object'}
                isRequired={isRequired}
                defaultState={isRequired || hasFieldValues}
                isCreated={hasFieldValues}
                description={field.description}
              >
                <EFormRenderer
                  schema={field.items}
                  path={`${fieldPath} #${arrIdx + 1}`}
                  level={level + 1}
                  parentProps={[...fieldParentProps, arrIdx]}
                  payload={payload}
                  arrIdx={arrIdx}
                />
              </EFormAccordion>
            ))
          : renderBaseFields(field, fieldPath, fieldType, isRequired, fieldParentProps, level)}
      </Fragment>
    )
  }

  const renderBaseFields = (field, fieldPath, fieldType, isRequired, fieldParentProps, level) => (
    <div
      key={fieldPath}
      className={
        fieldType === 'dynamic-dropdown'
          ? 'col-span-2 mt-1.25 mb-2.5 ml-3.75'
          : 'w-full mt-1.25 mb-2.5'
      }
    >
      <BaseEForm
        schema={field}
        required={isRequired}
        parentProps={fieldParentProps}
        payload={payload}
      />
    </div>
  )

  return (
    <div className="grid grid-cols-2 max-phone:grid-cols-1 gap-x-[30px] sm:gap-y-6 gap-y-4 items-start mb-1.25 w-full">
      {renderFields(schema?.properties, schema.required || [], path, parentProps, payload, level)}
    </div>
  )
}

EFormRenderer.propTypes = {
  schema: PropTypes.object,
  path: PropTypes.string,
  level: PropTypes.number,
  parentProps: PropTypes.array,
  payload: PropTypes.object,
  arrIdx: PropTypes.number,
}

export default EFormRenderer
